import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { Button, ListItem, Menu, Stack, TextField } from "~/common/mui";
import SettingsIcon from "@mui/icons-material/Settings";
import { ColumnConfigurator } from "./ColumnConfigurator";
import type { ColumnData } from "./ColumnConfigurator";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { useTranslation } from "react-i18next";

type PersistFn = (cols: ColumnData[], isReset: boolean) => void;

interface ConfigureColumnsPopoverProps {
  /** Text shown on the button */
  buttonLabel: string;
  /** Initial column list – could come from props or Redux */
  initialColumns: ColumnData[];
  /** Called when the user clicks “Save” */
  onSave: PersistFn;
  /** Called when the user clicks “Reset” */
  onReset: PersistFn;
  /** Optional: translation function for column headers */
  headerT?: (key: string) => string;
}

/** A reusable pop‑over that handles drafting, moving & visibility toggling */
export const ConfigureColumnsPopover: FC<ConfigureColumnsPopoverProps> = ({
  buttonLabel,
  initialColumns,
  onSave,
  onReset,
  headerT,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [draft, setDraft] = useState<ColumnData[]>(initialColumns);
  const [search, setSearch] = useState("");

  // reset draft each time the pop‑over opens
  useEffect(() => {
    if (open) {
      setDraft(initialColumns);
    }
  }, [open, initialColumns]);

  const moveColumn = useCallback((oldIdx: number, newIdx: number) => {
    setDraft((prev) => {
      const next = [...prev];
      const [item] = next.splice(oldIdx, 1);
      next.splice(newIdx, 0, item);
      return next;
    });
  }, []);

  const toggleVisibility = useCallback((idx: number, visible: boolean) => {
    setDraft((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], visible };
      return next;
    });
  }, []);

  const handleReset = () => {
    // let the consumer decide what “reset” means
    const resetCols = onReset(draft, true);
    // If the consumer returns a fresh list we can replace the draft,
    // otherwise just close the menu.
    if (Array.isArray(resetCols)) {
      setDraft(resetCols);
    }
    setOpen(false);
  };

  const handleSave = () => {
    onSave(draft, false);
    setOpen(false);
  };

  const filteredItems = useMemo(() => {
    if (!search.trim()) {
      return draft;
    }

    const lower = search.toLowerCase();

    return draft.filter((col) => (headerT ? headerT(col.label) : col.label).toLowerCase().includes(lower));
  }, [search, draft, headerT]);

  return (
    <>
      <Button variant="text" onClick={() => setOpen(true)} ref={anchorRef} startIcon={<SettingsIcon />}>
        {buttonLabel}
      </Button>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        slotProps={{
          list: {
            sx: {
              maxHeight: "580px",
              minWidth: "400px",
              overflowY: "auto",
              padding: 0,
              overscrollBehavior: "contain",
            },
          },
        }}
      >
        <Stack gap={1}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("manageColumns.placeholder")}
            sx={{ top: 0, ...stickyStyles }}
          />
          <ColumnConfigurator items={filteredItems} moveColumn={moveColumn} updateVisibility={toggleVisibility} />
          <ListItem component={Stack} direction="row" gap={1} sx={{ bottom: 0, ...stickyStyles }}>
            <Button fullWidth variant="text" onClick={handleReset}>
              {t("page.abmonitor.manageColumns.reset.text")}
            </Button>
            <Button fullWidth onClick={handleSave}>
              {t("page.abmonitor.manageColumns.save.text")}
            </Button>
          </ListItem>
        </Stack>
      </Menu>
    </>
  );
};

const stickyStyles = {
  p: 2,
  position: "sticky",
  backgroundColor: C_SURFACE,
  zIndex: 1,
};
